// @flow

import React from 'react';
import { I18n, Translate } from 'react-redux-i18n';
import { Bar } from 'react-chartjs-2';
import type { TopSCBean } from '../../../types/apolloType';
import { roundValue } from '../../../utils/formatUtils';
import { BL2Bean } from '../../../types/apolloType';

type Props = {
  data: TopSCBean[],
  branchToFocus: string,
  mercuryDataYear: number,
  caciDataYear: number,
}

const BAR_COLORS = [
  'rgb(248, 117, 166)',
  'rgb(232, 0, 28)',
  'rgb(146, 145, 255)',
  'rgb(123, 12, 145)',
  'rgb(255, 255, 206)',
  'rgb(120, 225, 255)',
  'rgb(149, 255, 13)',
  'rgb(255, 239, 88)',
  'rgb(12, 123, 256)',
  'rgb(192, 238, 186)',
  'rgb(252, 187, 82)',
  'rgb(214, 15, 123)',
  'rgb(193, 255, 255)',
  'rgb(251, 197, 255)',
  'rgb(219, 219, 128)',
  'rgb(145, 94, 47)',
];

function valuesOverBars(ctx, valuesDatasets, overlapDataset) {
  ctx.fillStyle = 'black';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  const MIN_TEXT_HEIGHT = 15; // minimal height where a text can be placed for this graph
  const TEXT_SPACE = 3; // space between the text and the bar below
  valuesDatasets.forEach((valuesDataset) => {
    for (let i = 0; i < valuesDataset.data.length; i++) {
      if (valuesDataset.data[i]) {
        const valuesMetaIndex = Object.keys(valuesDataset._meta)[0];
        const valuesModel = valuesDataset._meta[valuesMetaIndex].data[i]._model;
        const overlapMetaIndex = Object.keys(overlapDataset._meta)[0];
        const overlapModel = overlapDataset._meta[overlapMetaIndex].data[i]._model;
        const y = (valuesModel.y - overlapModel.y > 0 && valuesModel.y - overlapModel.y < MIN_TEXT_HEIGHT + overlapDataset.borderWidth.top)
          ? overlapModel.y - TEXT_SPACE
          : valuesModel.y - TEXT_SPACE;
        if (valuesDataset.totalTurnover) {
          ctx.fillText(roundValue(valuesDataset.totalTurnover), valuesModel.x, y);
        } else {
          ctx.fillText(roundValue(valuesDataset.data[i]), valuesModel.x, y);
        }
      }
    }
  });
}

function formatBarData(data: number, graphPosition: number) {
  const formatedData = [0, 0, 0, 0, 0];
  formatedData[graphPosition] = data;
  return formatedData;
}

function formatTurnover(topSc: TopSCBean, graphPosition: number, branchToFocus: string, stack: any) {
  let sum = 0;
  return topSc.listBL2.map((bL2: BL2Bean, index: number) => {
    sum += bL2.turnover;
    const dataset = {
      label: bL2.name,
      data: formatBarData(sum, graphPosition),
      // le 6 en dessous permet que les barres du graphe ne commencent pas par la même couleur
      backgroundColor: BAR_COLORS[((6 * graphPosition) + index) % BAR_COLORS.length],
      value: bL2.turnover,
      stack,
      isFocused: bL2.name === branchToFocus,
      barPercentage: 1,
      categoryPercentage: 0.4,
    };
    if (index === topSc.listBL2.length - 1) {
      dataset.totalTurnover = topSc.totalTurnover;
    }
    return dataset;
  });
}

class BestDealBarGraph extends React.Component<Props, State> {
  getTurnover = (stack: any) => this.props.data
    .flatMap((topSc: TopSCBean, index: number) =>
      formatTurnover(topSc, index, this.props.branchToFocus, stack));

  getLabelsCategories = () => this.props.data.map(entry => entry.name);

  getAverageTurnoverCountry = () => this.props.data.map(entry => entry.averageTurnoverCountry);

  getCaci = () => this.props.data.map(entry => entry.caci);

  getAverageCaciCountry = () => this.props.data.map(entry => entry.averageCaciCountry);

  render() {
    const STACK_1 = 1;
    const STACK_2 = 2;
    const data = {
      labels: this.getLabelsCategories(),
      datasets: [
        {
          label: I18n.t('bestDeals.country_average_mercury'),
          data: this.getAverageTurnoverCountry(),
          stack: STACK_1,
          borderWidth: {
            top: 5,
            bottom: 0,
            left: 0,
            right: 0,
          },
          backgroundColor: 'transparent',
          borderColor: 'black',
          barPercentage: 1,
          categoryPercentage: 0.4,
        },
        {
          label: I18n.t('bestDeals.country_average_caci'),
          data: this.getAverageCaciCountry(),
          stack: STACK_2,
          borderWidth: {
            top: 5, bottom: 0, left: 0, right: 0,
          },
          backgroundColor: 'transparent',
          borderColor: 'black',
          barPercentage: 1,
          categoryPercentage: 0.4,
        },
        {
          label: I18n.t(
            'bestDeals.catchment_expectation',
            { year: this.props.caciDataYear },
          ),
          data: this.getCaci(),
          backgroundColor: '#d6d6d6',
          stack: STACK_2,
          barPercentage: 1,
          categoryPercentage: 0.4,
        },
      ].concat(this.getTurnover(STACK_1)),
    };

    const options = {
      maintainAspectRatio: true,
      responsive: true,
      legend: {
        display: false,
      },
      layout: { padding: 20 },
      tooltips: {
        mode: 'x',
        filter(tooltipItem, metadata) {
          const value = metadata.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
          return value !== 0;
        },
        itemSort(tooltipItem1, tooltipItem2, metadata) {
          const value1 = metadata.datasets[tooltipItem1.datasetIndex].value;
          const value2 = metadata.datasets[tooltipItem2.datasetIndex].value;
          return value1 - value2;
        },
        callbacks: {
          label(context, metadata) {
            const value = (metadata.datasets[context.datasetIndex].value)
              ? metadata.datasets[context.datasetIndex].value
              : metadata.datasets[context.datasetIndex].data[context.index];
            const { label } = metadata.datasets[context.datasetIndex];
            return `${label}: ${roundValue(value)}`;
          },
          labelColor(tooltipItem, chart) {
            const oldColor = chart.data.datasets[tooltipItem.datasetIndex].backgroundColor;
            const newColor = (oldColor === 'transparent') ? 'black' : oldColor;
            return {
              borderColor: 'white',
              backgroundColor: newColor,
            };
          },
          labelTextColor(tooltipItem, chart) {
            const { isFocused } = chart.data.datasets[tooltipItem.datasetIndex];
            const color = chart.data.datasets[tooltipItem.datasetIndex].backgroundColor;
            return (isFocused) ? color : 'white';
          },
        },
      },
      scales: {
        xAxes: [
          {
            gridLines: {
              display: false,
              drawBorder: false,
            },
            stacked: true,
            ticks: {
              callback(value, index) {
                return (index === 0) ? [value, I18n.t('bestDeals.selected_label_information')] : [value];
              },
            },
          },
        ],
        yAxes: [
          {
            display: false,
            gridLines: {
              display: false,
              drawBorder: false,
            },
            stacked: false,
            ticks: { beginAtZero: true },
          },
        ],
      },
    };

    const plugins = [{
      afterDatasetsDraw(chartInstance) {
        valuesOverBars( // add values over bar in first stack
          chartInstance.chart.ctx,
          chartInstance.data.datasets.filter(cat => cat.totalTurnover),
          chartInstance.data.datasets.filter(cat => cat.backgroundColor === 'transparent' && cat.stack === STACK_1)[0],
        );
        valuesOverBars( // add values over bar in second stack
          chartInstance.chart.ctx,
          chartInstance.data.datasets.filter(cat => cat.backgroundColor !== 'transparent' && cat.stack === STACK_2),
          chartInstance.data.datasets.filter(cat => cat.backgroundColor === 'transparent' && cat.stack === STACK_2)[0],
        );
      },
    }];

    return (
      <>
        <Bar
          height={350}
          width={500}
          options={options}
          data={data}
          plugins={plugins}
        />
        <div className="bargraph-legend-container">
          <div className="bargraph-legend">
            <i className="fas fa-square top-level" />
            <Translate
              value="bestDeals.branch_expectation"
              year={this.props.mercuryDataYear}
            />
          </div>
          <div className="bargraph-legend">
            <i className="fas fa-square catchment" />
            <Translate
              value="bestDeals.catchment_expectation"
              year={this.props.caciDataYear}
            />
          </div>
          <div className="bargraph-legend">
            <i className="fas fa-square average" />
            <Translate value="bestDeals.country_average" />
          </div>
        </div>
      </>
    );
  }
}

export default BestDealBarGraph;
